import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAlertThresholds, THRESHOLD_KEYS, type ThresholdKey, type ThresholdConfig } from "@/hooks/useAlertThresholds";
import { Card, CardContent } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Loader2, Save } from "lucide-react";

// Human-readable labels — threshold_key values are stable config IDs
const THRESHOLD_LABELS: Record<ThresholdKey, { label: string; unit: string; description: string }> = {
  margin_drift_committed: { label: "Committed Margin Drift", unit: "pts", description: "Triggers when committed margin drops below estimated margin" },
  margin_drift_actual: { label: "Actual Margin Drift", unit: "pts", description: "Triggers when actual margin drops below estimated margin" },
  override_exposure_pct: { label: "Override Exposure", unit: "% of CV", description: "Rate override cost as percentage of contract value" },
  unattributed_cost_pct: { label: "Unattributed Cost", unit: "% of AC", description: "Unattributed expenses as percentage of actual cost" },
  sov_line_margin_floor: { label: "SOV Line Margin Floor", unit: "%", description: "Minimum acceptable margin per SOV line" },
  actual_vs_estimate_pct: { label: "Actual vs Estimate", unit: "%", description: "Actual cost as percentage of estimated cost" },
  co_margin_dilution: { label: "CO Margin Dilution", unit: "pts", description: "Margin impact from change orders" },
};

export const AlertThresholdsTab = () => {
  const qc = useQueryClient();
  const { thresholds, defaults, isLoading } = useAlertThresholds();
  const [edits, setEdits] = useState<Record<string, Partial<ThresholdConfig>>>({});
  const [saving, setSaving] = useState(false);

  const getValue = (key: ThresholdKey, field: "warning" | "critical" | "active") => {
    if (edits[key]?.[field] !== undefined) return edits[key][field];
    return thresholds[key]?.[field] ?? defaults[key][field];
  };

  const setEdit = (key: ThresholdKey, field: keyof ThresholdConfig, value: number | boolean) => {
    setEdits((prev) => ({
      ...prev,
      [key]: { ...prev[key], [field]: value },
    }));
  };

  const hasEdits = Object.keys(edits).length > 0;

  const handleSave = async () => {
    setSaving(true);
    try {
      const { data: companyId } = await supabase.rpc("my_company_id");
      if (!companyId) throw new Error("Company not found");

      // Upsert all threshold keys (edited or not — ensures complete config)
      const rows = THRESHOLD_KEYS.map((key) => ({
        company_id: companyId,
        threshold_key: key,
        warning_value: getValue(key, "warning") as number,
        critical_value: getValue(key, "critical") as number,
        is_active: getValue(key, "active") as boolean,
        updated_at: new Date().toISOString(),
      }));

      const { error } = await (supabase
        .from("alert_thresholds" as any) as any)
        .upsert(rows, { onConflict: "company_id,threshold_key" });
      if (error) throw error;

      qc.invalidateQueries({ queryKey: ["alert_thresholds"] });
      setEdits({});
      toast.success("Alert thresholds saved");
    } catch (err: any) {
      toast.error(err.message ?? "Failed to save thresholds");
    } finally {
      setSaving(false);
    }
  };

  if (isLoading) {
    return <div className="flex items-center gap-2 text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>;
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold">Alert Thresholds</h2>
          <p className="text-sm text-muted-foreground">
            Configure warning and critical thresholds for financial alerts.
          </p>
        </div>
        <Button size="sm" disabled={!hasEdits || saving} onClick={handleSave}>
          {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Save className="h-4 w-4 mr-1" />}
          Save Changes
        </Button>
      </div>

      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Threshold</TableHead>
                <TableHead className="text-center w-[100px]">Warning</TableHead>
                <TableHead className="text-center w-[100px]">Critical</TableHead>
                <TableHead className="text-center w-[60px]">Unit</TableHead>
                <TableHead className="text-center w-[80px]">Active</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {THRESHOLD_KEYS.map((key) => {
                const meta = THRESHOLD_LABELS[key];
                return (
                  <TableRow key={key}>
                    <TableCell>
                      <div>
                        <span className="text-sm font-medium">{meta.label}</span>
                        <p className="text-xs text-muted-foreground">{meta.description}</p>
                      </div>
                    </TableCell>
                    <TableCell className="text-center">
                      <Input
                        type="number"
                        className="w-20 mx-auto text-center h-8 text-sm"
                        value={getValue(key, "warning") as number}
                        onChange={(e) => setEdit(key, "warning", Number(e.target.value))}
                      />
                    </TableCell>
                    <TableCell className="text-center">
                      <Input
                        type="number"
                        className="w-20 mx-auto text-center h-8 text-sm"
                        value={getValue(key, "critical") as number}
                        onChange={(e) => setEdit(key, "critical", Number(e.target.value))}
                      />
                    </TableCell>
                    <TableCell className="text-center text-xs text-muted-foreground">
                      {meta.unit}
                    </TableCell>
                    <TableCell className="text-center">
                      <Switch
                        checked={getValue(key, "active") as boolean}
                        onCheckedChange={(v) => setEdit(key, "active", v)}
                      />
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
};
